import { MODAL_ROUTES, useNavigateModal } from "@/app/router";
import type { Document } from "@/shared/domains/documents";
import { READ_ONLY_DOCUMENT_FOLDER } from "@/shared/models/legacy-constants";
import type {
  Folder,
  ReadOnlyDocumentFolder,
} from "@/shared/models/legacy-types";
import {
  CalendarMinimalisticIcon,
  EyeIcon,
  IconWrapper,
  UploadedFileCard,
} from "@/shared/ui";
import { formatBackendDate } from "@/shared/utils";
import { DOCUMENT_ICON } from "../components.constants";
import { EditActions } from "./edit-actions";

interface UploadedDocumentProps {
  folder: Folder;
  file: Document;
  readOnly?: boolean;
}

const isReadOnlyType = (x: Folder): x is ReadOnlyDocumentFolder => {
  return Object.values(READ_ONLY_DOCUMENT_FOLDER).includes(
    x as ReadOnlyDocumentFolder,
  );
};

export const UploadedDocument = ({
  folder,
  file,
  readOnly,
}: UploadedDocumentProps) => {
  const navigateModal = useNavigateModal();
  const vendorParam = file.vendor
    ? `?vendor=${encodeURIComponent(file.vendor)}`
    : "";

  return (
    <UploadedFileCard.Container
      key={file.id}
      onClick={() =>
        navigateModal(
          `${MODAL_ROUTES.DOCUMENT_CENTER.PREVIEW_DOCUMENT}/${folder}/${file.id}${vendorParam}`,
        )
      }
      className="cursor-pointer"
    >
      <UploadedFileCard.Content className="w-full max-w-full flex-row items-center justify-between md:max-w-full">
        <div className="flex max-w-36 items-center gap-2 md:max-w-96">
          <IconWrapper size="sm" className="text-neutral-900">
            {DOCUMENT_ICON[folder]}
          </IconWrapper>
          <UploadedFileCard.Title>{file.name}</UploadedFileCard.Title>
        </div>

        <div className="flex items-center gap-2">
          <IconWrapper size="sm" className="text-neutral-600">
            <CalendarMinimalisticIcon />
          </IconWrapper>
          <UploadedFileCard.Description>
            {formatBackendDate(file.createdAt)}
          </UploadedFileCard.Description>
        </div>
      </UploadedFileCard.Content>

      <UploadedFileCard.Actions onClick={(e) => e.stopPropagation()}>
        {isReadOnlyType(folder) || readOnly ? (
          <IconWrapper
            as="button"
            type="button"
            size="sm"
            onClick={() =>
              navigateModal(
                `${MODAL_ROUTES.DOCUMENT_CENTER.PREVIEW_DOCUMENT}/${folder}/${file.id}${vendorParam}`,
              )
            }
            className="text-primary-900 focus:outline-secondary-200"
          >
            <EyeIcon />
          </IconWrapper>
        ) : (
          <EditActions folder={folder} fileId={file.id} vendor={file.vendor} />
        )}
      </UploadedFileCard.Actions>
    </UploadedFileCard.Container>
  );
};
